/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useState} from 'react'
import {useDispatch, useSelector, shallowEqual} from 'react-redux'
import {RootState} from '../../../setup'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {IAgentsState} from './redux/AgentsRedux'
import {actions} from './redux/AgentsRedux'
import {AgentModel} from './models/AgentModel'
import {PageTitle} from '../../../_metronic/layout/core'
import AgentRow from './AgentRow'
import {Modal} from 'react-bootstrap'

const AgentsPage: React.FC = () => {
  const dispatch = useDispatch()
  const [showModal, setShowModal] = useState(false)

  const agents: IAgentsState = useSelector<RootState>(
    ({agents}) => agents,
    shallowEqual
  ) as IAgentsState

  useEffect(() => {
    dispatch(actions.fetchPage(1))
  }, [dispatch])

  const changePage = (page: number) => dispatch(actions.fetchPage(page))

  const getPageList = () => {
    let elements: JSX.Element[] = []
    agents.visiblePageList.forEach((page) => {
      elements.push(
        <li key={page} className={page === agents.currentPage ? 'page-item active' : 'page-item'}>
          <a href='#' className='page-link' onClick={() => changePage(page)}>
            {page}
          </a>
        </li>
      )
    })
    return elements
  }

  const getNextPageButton = () => {
    if (agents.visiblePageList[agents.visiblePageList.length - 1] < agents.totalPages) {
      return (
        <li className='page-item next'>
          <a href='#' onClick={() => changePage(++agents.currentPage)} className='page-link'>
            <i className='next'></i>
          </a>
        </li>
      )
    }
  }

  const getPreviousPageButton = () => {
    if (agents.visiblePageList[0] > 1) {
      return (
        <li className='page-item previous'>
          <a href='#' onClick={() => changePage(--agents.currentPage)} className='page-link'>
            <i className='previous'></i>
          </a>
        </li>
      )
    }
  }

  const getEmptyTable = () => {
    if (!agents.agents || agents.agents.length === 0) {
      return (
        <div className='row justify-content-md-center mt-20 mb-20'>
          <div className='col-md-auto'>No agents found. Add your first agent.</div>
        </div>
      )
    }
  }

  const getRows = () => {
    let elements: JSX.Element[] = []
    if (agents.agents && agents.agents.length > 0) {
      agents.agents.forEach((agent: AgentModel, index: number) => {
        elements.push(
          <AgentRow key={index} agent={agent} index={`${agents.currentPage}-${index}`} />
        )
      })
      return elements
    }
  }

  const addAgentSchema = Yup.object().shape({
    name: Yup.string()
      .min(3, 'Minimum 3 characters')
      .max(50, 'Maximum 50 characters')
      .required('Name is required'),
    email: Yup.string().email('Invalid email format').required('Email is required'),
    password: Yup.string().min(8, 'Minimum 8 characters').required('Password is required'),
  })

  const initialValues = {
    name: '',
    email: '',
    password: '',
  }

  const formik = useFormik({
    initialValues,
    validationSchema: addAgentSchema,
    onSubmit: (values, {setSubmitting, resetForm}) => {
      dispatch(actions.createAgent(values.name, values.email, values.password))
      setSubmitting(false)
      resetForm()
      setShowModal(false)
    },
  })

  return (
    <>
      <PageTitle breadcrumbs={[]}>{'Agents'}</PageTitle>
      <div className={`card mb-5 mb-xl-8`}>
        {/* begin::Header */}
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>Agents</span>
          </h3>
          <div className='card-toolbar'>
            <button className='btn btn-sm btn-primary' onClick={() => setShowModal(true)}>
              Add New Agent
            </button>
          </div>
        </div>
        {/* end::Header */}
        {/* begin::Body */}
        <div className='card-body py-3'>
          {getEmptyTable()}
          {/* begin::Table container */}
          <div className='table-responsive'>
            {/* begin::Table */}
            <table className='table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3'>
              {/* begin::Table head */}
              <thead>
                <tr className='fw-bolder text-muted'>
                  <th className='min-w-150px'>Name</th>
                  <th className='min-w-150px'>Email</th>
                  <th className='min-w-150px'>Created On</th>
                  <th className='text-end min-w-100px'>Actions</th>
                </tr>
              </thead>
              {/* end::Table head */}
              {/* begin::Table body */}
              <tbody>{getRows()}</tbody>
              {/* end::Table body */}
            </table>
            {/* end::Table */}
          </div>
          {/* end::Table container */}
          {agents.totalPages > 1 && (
            <div className='d-flex flex-stack flex-wrap pt-10'>
              <div className='fs-6 fw-bold text-gray-700'>
                Showing {(agents.currentPage - 1) * agents.agentsPerPage + 1} to{' '}
                {Math.min(agents.currentPage * agents.agentsPerPage, agents.totalAgents)} of{' '}
                {agents.totalAgents} entries
              </div>

              <ul className='pagination'>
                {getPreviousPageButton()}
                {getPageList()}
                {getNextPageButton()}
              </ul>
            </div>
          )}
        </div>
        {/* begin::Body */}
      </div>

      {/* Add Agent Modal */}
      <Modal
        show={showModal}
        onHide={() => {
          setShowModal(false)
          formik.resetForm()
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add New Agent</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={formik.handleSubmit} noValidate>
            <div className='fv-row mb-8'>
              <label className='form-label fw-bolder text-dark fs-6'>Name</label>
              <input
                placeholder='Name'
                type='text'
                autoComplete='off'
                {...formik.getFieldProps('name')}
                className={`form-control bg-transparent ${
                  formik.touched.name && formik.errors.name ? 'is-invalid' : ''
                }`}
              />
              {formik.touched.name && formik.errors.name && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.name}</span>
                  </div>
                </div>
              )}
            </div>

            <div className='fv-row mb-8'>
              <label className='form-label fw-bolder text-dark fs-6'>Email</label>
              <input
                placeholder='Email'
                type='email'
                autoComplete='off'
                {...formik.getFieldProps('email')}
                className={`form-control bg-transparent ${
                  formik.touched.email && formik.errors.email ? 'is-invalid' : ''
                }`}
              />
              {formik.touched.email && formik.errors.email && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.email}</span>
                  </div>
                </div>
              )}
            </div>

            <div className='fv-row mb-8'>
              <label className='form-label fw-bolder text-dark fs-6'>Password</label>
              <input
                placeholder='Password'
                type='password'
                autoComplete='off'
                {...formik.getFieldProps('password')}
                className={`form-control bg-transparent ${
                  formik.touched.password && formik.errors.password ? 'is-invalid' : ''
                }`}
              />
              {formik.touched.password && formik.errors.password && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.password}</span>
                  </div>
                </div>
              )}
            </div>

            <div className='text-center'>
              <button
                type='submit'
                className='btn btn-lg btn-primary w-100 mb-5'
                disabled={formik.isSubmitting || !formik.isValid}
              >
                {!formik.isSubmitting && <span className='indicator-label'>Add</span>}
                {formik.isSubmitting && (
                  <span className='indicator-progress d-block'>
                    Please wait...
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                )}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  )
}

export default AgentsPage
